import { type AuthFailure } from './auth-errors.js';
import { AuthCodewords } from '@airreading/common/error';
import * as Sentry from '@sentry/react';

const reportedCauses = new WeakSet<object>();

/** Best-effort reporting, once per error object, including when React replays an effect. */
export function reportAuthFailure(failure: AuthFailure): void {
  const failureCase =
    failure.code === 'auth_network_error'
      ? 'network'
      : failure.code === 'auth_air_unavailable'
        ? 'api'
        : failure.code === 'auth_csrf_invalid'
          ? 'csrf'
          : undefined;
  if (!failureCase || !Sentry.isEnabled()) return;

  const cause = failure.cause;
  if (typeof cause === 'object' && cause !== null) {
    if (reportedCauses.has(cause)) return;
    reportedCauses.add(cause);
  }

  const context = {
    case: failureCase,
    code: failure.code,
    codeword: AuthCodewords[failure.code],
    ...(failure.email ? { email: failure.email } : {}),
    ...(failure.identityProviderUserId ? { identityProviderUserId: failure.identityProviderUserId } : {}),
    loginFlow: failure.flow ?? 'unknown',
    timestamp: new Date().toISOString(),
    ...(failure.userId ? { userId: failure.userId } : {}),
  };
  const tags = {
    case: context.case,
    code: context.code,
    codeword: context.codeword,
    component: 'auth',
    loginFlow: context.loginFlow,
  };
  const user = {
    ...(failure.email ? { email: failure.email } : {}),
    ...(failure.userId ? { id: failure.userId } : {}),
  };

  try {
    Sentry.withScope((scope) => {
      // Scope processors run after SDK processors. Allowlist this event's payload
      // so automatic request URLs, referrers, breadcrumbs, and stale user data
      // cannot carry tokens or secrets into the dedicated login report.
      scope.addEventProcessor((event) => ({
        contexts: { auth: context },
        dist: event.dist,
        environment: event.environment,
        event_id: event.event_id,
        fingerprint: event.fingerprint,
        level: event.level,
        message: event.message,
        platform: event.platform,
        release: event.release,
        sdk: event.sdk,
        tags,
        timestamp: event.timestamp,
        user,
      }));
      Sentry.captureMessage('Air login failed', {
        fingerprint: ['air-login-failure', failureCase],
        level: 'error',
        tags,
      });
    });
  } catch {
    // Telemetry must never prevent the error screen from rendering.
  }
}
